import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Bot, CheckCircle2, AlertTriangle, Loader2, RefreshCw, Zap } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Owner-only Roni health + soft-reboot panel. Rendered on /admin dashboard.
 *
 * Behaviour:
 *   - On mount + every 30s: GET /api/admin/roni/health (synthetic ping through
 *     the full chat stack; 12s timeout on the backend). Renders green/red pill.
 *   - "Reboot Roni" -> POST /api/admin/roni/reboot. Backend invalidates the FX
 *     cache, rebuilds the google-genai client if a valid AIza* key is present,
 *     then re-runs the health check and returns before/after health.
 *   - Zero risk to prior guardrails: no prompt changes, no data changes, no
 *     tool-router changes. Only in-memory state (FX cache + client handle).
 */
export default function RoniHealthPanel() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rebooting, setRebooting] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/roni/health");
      setHealth(r.data);
    } catch (err) {
      setHealth({ ok: false, error: err?.response?.data?.detail || "health check failed" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 30_000);
    return () => clearInterval(pollRef.current);
  }, [load]);

  const reboot = async () => {
    if (!window.confirm("Soft-reboot Roni? Clears FX cache and rebuilds the LLM client. Safe, but next reply may take 2-3s longer.")) return;
    setRebooting(true);
    try {
      const r = await api.post("/admin/roni/reboot");
      const after = r.data?.health_after;
      if (after?.ok) {
        toast.success(`Roni rebooted. Health OK (${after.chunks} chunks, ${after.latency_ms}ms, provider=${after.provider}).`);
      } else {
        toast.error(`Reboot ran but health still failing: ${after?.error || "unknown"}`, { duration: 10000 });
      }
      setHealth(after || null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reboot failed.");
    } finally {
      setRebooting(false);
    }
  };

  const ok = !!health?.ok;
  const tone = loading && !health
    ? "border-slate-200 bg-slate-50"
    : ok
      ? "border-emerald-300 bg-emerald-50"
      : "border-red-300 bg-red-50";

  return (
    <div className="mt-8 bg-white rounded-2xl border hairline p-6" data-testid="roni-health-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Bot className="w-5 h-5 text-propertic-gold" />
          <h2 className="font-editorial text-2xl text-propertic-navy">Roni AI health</h2>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 h-9 px-3 rounded-full border hairline text-[11px] font-mono-brand tracking-[0.14em] uppercase text-propertic-navy hover:bg-propertic-cream disabled:opacity-50"
          data-testid="roni-refresh-btn"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Refresh
        </button>
      </div>

      <div className={`rounded-xl border ${tone} p-4 grid sm:grid-cols-4 gap-4`} data-testid="roni-health-summary">
        <StatCell
          label="Status"
          value={
            loading && !health ? "Checking…" : ok ? "Healthy" : "Failing"
          }
          icon={ok ? CheckCircle2 : AlertTriangle}
          tone={ok ? "green" : "red"}
        />
        <StatCell label="Chunks" value={health?.chunks ?? "—"} />
        <StatCell label="Latency" value={health?.latency_ms != null ? `${health.latency_ms} ms` : "—"} />
        <StatCell label="Provider" value={health?.provider || "—"} />
      </div>

      {health?.error && (
        <p className="mt-3 text-[12px] text-red-700 font-mono-brand break-words" data-testid="roni-health-error">
          Error: {health.error}
        </p>
      )}

      <div className="mt-5 flex items-center justify-between">
        <p className="text-[11px] text-propertic-muted font-mono-brand">
          Synthetic "ping" fired through the full chat stack every 30s. Reboot clears FX cache + rebuilds the LLM client (in-memory only, no pod restart).
        </p>
        <button
          type="button"
          onClick={reboot}
          disabled={rebooting}
          className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-propertic-navy text-propertic-paper text-[11px] font-mono-brand tracking-[0.14em] uppercase hover:bg-propertic-navy-2 disabled:opacity-50"
          data-testid="roni-reboot-btn"
        >
          {rebooting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
          {rebooting ? "Rebooting…" : "Reboot Roni"}
        </button>
      </div>
    </div>
  );
}

function StatCell({ label, value, icon: Icon, tone }) {
  const toneClass = tone === "green"
    ? "text-emerald-700"
    : tone === "red"
      ? "text-red-700"
      : "text-propertic-navy";
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1">
        {Icon && <Icon className={`w-3.5 h-3.5 ${toneClass}`} />}
        <p className="font-mono-brand text-[10px] tracking-[0.14em] uppercase text-propertic-muted">{label}</p>
      </div>
      <p className={`font-editorial text-xl tabular ${toneClass}`}>{value}</p>
    </div>
  );
}
